import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { 
  ArrowLeft, Edit, FormInput, Globe, DollarSign, Users, 
  Settings, Layout, TrendingUp, Calendar, Link2, Copy, ExternalLink,
  BarChart3, Download, X, Loader2, UserPlus, Shield, Bell,
  Archive, RotateCcw, AlertTriangle, CheckCircle, FileText, XCircle,
  ChevronDown, ChevronUp, Mail, Phone, Upload, Banknote
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { TeamMemberModal } from '../components/CampaignModals';
import FundManagement from '../components/FundManagement';
import EntityHistory from '../components/EntityHistory';

const CampaignDetail = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [activating, setActivating] = useState(false);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [selectedPaymentAccount, setSelectedPaymentAccount] = useState('');
  const [savingPayment, setSavingPayment] = useState(false);
  
  // Team member modal state
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  
  // Short URLs state
  const [shortUrls, setShortUrls] = useState({});

  useEffect(() => {
    fetchCampaignData();
  }, [campaignId]);

  useEffect(() => {
    if (campaign?.id) {
      fetchShortUrls();
    }
  }, [campaign?.id]);

  const fetchShortUrls = async () => {
    try {
      const response = await api.get(`/short-urls/campaign/${campaignId}`);
      setShortUrls(response.data);
    } catch (error) {
      // Short URLs not found is fine
    }
  };

  const generateShortUrl = async (urlType) => {
    try {
      const originalUrl = urlType === 'registration' 
        ? `${window.location.origin}/register/${campaign.slug}`
        : `${window.location.origin}/kiosk/${campaign.id}`;
      
      const response = await api.post('/short-urls', {
        original_url: originalUrl,
        campaign_id: campaignId,
        url_type: urlType
      });
      
      setShortUrls(prev => ({
        ...prev,
        [urlType]: response.data
      }));
      
      toast.success('Short link generated!');
    } catch (error) {
      toast.error('Failed to generate short link');
    }
  };

  const fetchCampaignData = async () => {
    try {
      const [campaignRes, analyticsRes] = await Promise.all([
        api.get(`/campaigns/${campaignId}`),
        api.get(`/campaigns/${campaignId}/analytics`).catch(() => null)
      ]);
      
      setCampaign(campaignRes.data);
      setSelectedPaymentAccount(campaignRes.data.payment_account_id || '');
      if (analyticsRes) {
        setAnalytics(analyticsRes.data);
      }
      
      // Fetch team members (users with access to this campaign)
      try {
        const usersRes = await api.get('/users');
        const members = usersRes.data.filter(u => 
          u.role === 'org_admin' && u.campaign_access?.includes(campaignId)
        );
        setTeamMembers(members);
      } catch (e) {
        // Ignore if not super admin
      }
    } catch (error) {
      toast.error('Failed to load campaign data');
    } finally {
      setLoading(false);
    }
  };

  const [selectedGateway, setSelectedGateway] = useState('');

  const openPaymentModal = async () => {
    try {
      const response = await api.get('/payment-accounts');
      setPaymentAccounts(response.data);
      setSelectedGateway(campaign.payment_gateway || 'razorpay');
      setShowPaymentModal(true);
    } catch (error) {
      toast.error('Failed to load payment accounts');
    }
  };

  // Filter accounts by selected gateway
  const filteredAccounts = paymentAccounts.filter(
    account => account.gateway_type === selectedGateway
  );

  const savePaymentSettings = async () => {
    setSavingPayment(true);
    try {
      await api.put(`/campaigns/${campaignId}`, {
        payment_account_id: selectedPaymentAccount || null,
        payment_gateway: selectedGateway || 'none'
      });
      
      // Refresh campaign data
      const res = await api.get(`/campaigns/${campaignId}`);
      setCampaign(res.data);
      
      setShowPaymentModal(false);
      toast.success('Payment settings updated!');
    } catch (error) {
      toast.error('Failed to update payment settings');
    } finally {
      setSavingPayment(false);
    }
  };

  const openTeamModal = () => {
    setShowTeamModal(true);
  };

  const fetchTeamMembers = async () => {
    try {
      const usersRes = await api.get('/users');
      const members = usersRes.data.filter(u => 
        u.role === 'org_admin' && u.campaign_access?.includes(campaignId)
      );
      setTeamMembers(members);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    }
  };

  const copyPublicUrl = () => {
    const url = `${window.location.origin}/register/${campaign.slug}`;
    navigator.clipboard.writeText(url);
    toast.success('URL copied to clipboard!');
  };

  const handleArchive = async () => {
    setArchiving(true);
    try {
      await api.post(`/campaigns/${campaignId}/archive`);
      toast.success('Event archived successfully');
      setShowArchiveModal(false);
      fetchCampaignData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive event');
    } finally {
      setArchiving(false);
    }
  };

  const handleUnarchive = async () => {
    setArchiving(true);
    try {
      await api.post(`/campaigns/${campaignId}/unarchive`);
      toast.success('Event restored successfully');
      fetchCampaignData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore event');
    } finally {
      setArchiving(false);
    }
  };

  const handleActivate = async () => {
    setActivating(true);
    try {
      await api.put(`/campaigns/${campaignId}`, { status: 'active' });
      toast.success('Event activated successfully');
      fetchCampaignData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate event');
    } finally {
      setActivating(false);
    }
  };

  const handleToggleLandingPage = async (enabled) => {
    try {
      await api.put(`/campaigns/${campaignId}`, { has_landing_page: enabled });
      toast.success(enabled ? 'Landing page enabled' : 'Landing page disabled - visitors will go directly to registration form');
      fetchCampaignData();
    } catch (error) {
      toast.error('Failed to update landing page setting');
    }
  };

  const getPublicUrl = () => {
    if (campaign?.has_landing_page) {
      return `/event/${campaign.slug}`;
    }
    return `/register/${campaign.slug}`;
  };

  const COLORS = ['#14b8a6', '#f97316', '#ef4444'];

  const paymentStatusData = analytics ? [
    { name: 'Paid', value: analytics.paid_registrations || 0 },
    { name: 'Pending', value: analytics.pending_registrations || 0 },
    { name: 'Failed', value: analytics.failed_registrations || 0 }
  ].filter(d => d.value > 0) : [];

  const chartData = analytics?.registration_by_date?.map(item => ({
    date: format(parseISO(item.date), 'MMM dd'),
    count: item.count
  })) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-jakarta">Loading event...</p>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-slate-600 font-jakarta">Event not found</p>
          <Button onClick={() => navigate('/campaigns')} className="mt-4">
            Back to Events
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-10 backdrop-blur-md bg-white/80">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/campaigns')}
              data-testid="back-button"
              className="hover:bg-slate-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-outfit font-bold text-slate-900">{campaign.name}</h1>
                <span className={`px-3 py-1 rounded-full text-xs font-jakarta font-medium ${
                  campaign.status === 'active'
                    ? 'bg-green-100 text-green-700'
                    : campaign.status === 'draft'
                    ? 'bg-slate-100 text-slate-600'
                    : 'bg-red-100 text-red-600'
                }`}>
                  {campaign.status}
                </span>
                {campaign.fund_collection_mode && (
                  <span className={`px-3 py-1 rounded-full text-xs font-jakarta font-medium ${
                    campaign.fund_collection_mode === 'kaizen_managed'
                      ? 'bg-violet-100 text-violet-700'
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {campaign.fund_collection_mode === 'kaizen_managed' 
                      ? '💰 Kaizen Managed' 
                      : '🏦 Direct to Organizer'}
                  </span>
                )}
              </div>
              <p className="text-sm font-jakarta text-slate-600 mt-1">{campaign.organization_name}</p>
            </div>
            {isSuperAdmin() && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => navigate(`/campaigns/${campaignId}/edit`)}
                  className="border-slate-200 hover:bg-slate-50"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button
                  onClick={() => navigate(`/campaigns/${campaignId}/form-builder`)}
                  data-testid="form-builder-button"
                  className="bg-primary hover:bg-primary-hover text-primary-foreground"
                >
                  <FormInput className="w-4 h-4 mr-2" />
                  Form Builder
                </Button>
                <Button
                  onClick={() => navigate(`/campaigns/${campaignId}/email-templates`)}
                  variant="outline"
                  data-testid="email-templates-button"
                  className="border-slate-200 hover:bg-slate-50"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Email Templates
                </Button>
                {campaign.campaign_type === 'closed_group' && (
                  <Button
                    onClick={() => navigate(`/campaigns/${campaignId}/members`)}
                    variant="outline"
                    className="border-primary text-primary hover:bg-primary/10"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Manage Members
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-jakarta text-slate-500 mb-1">Registrations</p>
                <h3 className="text-3xl font-outfit font-bold text-slate-900">
                  {campaign.current_registrations}
                </h3>
                {campaign.max_registrations && (
                  <div className="mt-2">
                    <div className="w-full bg-slate-100 rounded-full h-1.5">
                      <div 
                        className="bg-primary h-1.5 rounded-full" 
                        style={{ width: `${Math.min(100, (campaign.current_registrations / campaign.max_registrations) * 100)}%` }}
                      />
                    </div>
                    <p className="text-xs font-jakarta text-slate-500 mt-1">
                      {campaign.max_registrations - campaign.current_registrations} spots left
                    </p>
                  </div>
                )}
              </div>
              <div className="p-3 rounded-lg bg-primary/10">
                <Users className="w-5 h-5 text-primary" />
              </div>
            </div>
          </Card>

          <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-jakarta text-slate-500 mb-1">Revenue</p>
                <h3 className="text-3xl font-outfit font-bold text-slate-900">
                  ₹{(campaign.total_revenue * 0.9705).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </h3>
                {analytics && analytics.average_registration_value > 0 && (
                  <p className="text-xs font-jakarta text-slate-500 mt-1">
                    Avg: ₹{(analytics.average_registration_value * 0.9705).toFixed(0)}
                  </p>
                )}
              </div>
              <div className="p-3 rounded-lg bg-secondary/10">
                <DollarSign className="w-5 h-5 text-secondary" />
              </div>
            </div>
          </Card>

          <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-jakarta text-slate-500 mb-1">Paid</p>
                <h3 className="text-3xl font-outfit font-bold text-green-600">
                  {analytics?.paid_registrations || 0}
                </h3>
                <p className="text-xs font-jakarta text-slate-500 mt-1">
                  {analytics?.pending_registrations || 0} pending, {analytics?.failed_registrations || 0} failed
                </p>
              </div>
              <div className="p-3 rounded-lg bg-green-100">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </Card>

          {/* Payment Gateway Card - Super Admin Only */}
          {isSuperAdmin() && (
            <Card 
              className="bg-white rounded-xl border border-slate-100 shadow-card p-5 cursor-pointer hover:border-primary/30 hover:shadow-md transition-all"
              onClick={openPaymentModal}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-jakarta text-slate-500 mb-1">Payment Gateway</p>
                  <h3 className="text-xl font-outfit font-bold text-slate-900 capitalize">
                    {campaign.payment_gateway || 'Not Set'}
                  </h3>
                  {campaign.payment_purpose && (
                    <p className="text-xs font-jakarta text-slate-500 mt-1">
                      {campaign.payment_purpose}
                    </p>
                  )}
                  <p className="text-xs text-primary mt-1">Click to configure →</p>
                </div>
                <div className="p-3 rounded-lg bg-accent/10 hover:bg-accent/20 transition-colors">
                  <Settings className="w-5 h-5 text-accent" />
                </div>
              </div>
            </Card>
          )}

          {/* View Registrations Card - For Org Admin (when Payment Gateway is hidden) */}
          {!isSuperAdmin() && (
            <Card 
              className="bg-white rounded-xl border border-slate-100 shadow-card p-5 cursor-pointer hover:border-primary/30 hover:shadow-md transition-all"
              onClick={() => navigate(`/campaigns/${campaignId}/registrations`)}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-jakarta text-slate-500 mb-1">View All</p>
                  <h3 className="text-xl font-outfit font-bold text-slate-900">
                    Registrations
                  </h3>
                  <p className="text-xs text-primary mt-1">Click to view →</p>
                </div>
                <div className="p-3 rounded-lg bg-primary/10">
                  <FileText className="w-5 h-5 text-primary" />
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Registration Trend */}
          <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-6 lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-outfit font-semibold text-slate-900">Registration Trend</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/campaigns/${campaignId}/registrations`)}
                className="text-xs"
              >
                View All →
              </Button>
            </div>
            {chartData.length > 0 ? (
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorReg" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#7c3aed" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <Tooltip />
                    <Area 
                      type="monotone" 
                      dataKey="count" 
                      stroke="#7c3aed" 
                      strokeWidth={2}
                      fillOpacity={1} 
                      fill="url(#colorReg)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-52 flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <BarChart3 className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No registration data yet</p>
                </div>
              </div>
            )}
          </Card>

          {/* Payment Status */}
          <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-6">
            <h2 className="text-lg font-outfit font-semibold text-slate-900 mb-4">Payment Status</h2>
            {paymentStatusData.length > 0 ? (
              <div className="flex items-center justify-between">
                <div className="h-48 w-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={paymentStatusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={65}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {paymentStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                {/* Legend */}
                <div className="flex flex-col gap-3">
                  {paymentStatusData.map((entry, index) => (
                    <div key={entry.name} className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="text-sm font-jakarta text-slate-600">
                        {entry.name}: <span className="font-semibold text-slate-900">{entry.value}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-52 flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <DollarSign className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No payment data yet</p>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Details & Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Campaign Details */}
          <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-6 lg:col-span-2">
            <h2 className="text-lg font-outfit font-semibold text-slate-900 mb-6">Event Details</h2>
            
            {/* Public URL */}
            <div className="mb-4 p-4 bg-slate-50 rounded-lg">
              <p className="text-sm font-jakarta font-medium text-slate-500 mb-2">Public Registration URL</p>
              
              {/* Original URL */}
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-1 min-w-0 flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-slate-200 overflow-hidden">
                  <Link2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span className="text-sm font-mono text-slate-700 truncate block">
                    {`${window.location.origin}/register/${campaign.slug}`}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/register/${campaign.slug}`);
                    toast.success('URL copied!');
                  }}
                  className="shrink-0"
                  title="Copy URL"
                >
                  <Copy className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(`/register/${campaign.slug}`, '_blank')}
                  className="shrink-0"
                  title="Open URL"
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>

              {/* Short URL - Separate Line */}
              {shortUrls?.registration ? (
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0 flex items-center gap-2 px-3 py-2 bg-primary/5 rounded-lg border border-primary/20 overflow-hidden">
                    <Link2 className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm font-mono text-primary font-medium truncate block">
                      {shortUrls.registration.short_url}
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(shortUrls.registration.short_url);
                      toast.success('Short URL copied!');
                    }}
                    className="shrink-0 border-primary/30 text-primary hover:bg-primary/10"
                    title="Copy Short URL"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => generateShortUrl('registration')}
                  className="text-xs text-primary p-0 h-auto"
                >
                  Generate short link
                </Button>
              )}
            </div>

            {/* Kiosk Mode URL - Show for offline payment campaigns, kiosk_enabled online campaigns, or legacy offline payments config */}
            {(campaign.payment_collection_method === 'offline' || campaign.kiosk_enabled || campaign.payment_config?.allow_offline_payments) && (
              <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                <p className="text-sm font-jakarta font-medium text-slate-500 mb-2 flex items-center gap-2">
                  <Banknote className="w-4 h-4" />
                  Kiosk Mode URL
                  {campaign.payment_collection_method === 'online' && campaign.kiosk_enabled && (
                    <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">Hybrid Mode</span>
                  )}
                </p>
                
                {/* Original URL */}
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex-1 min-w-0 flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-slate-200 overflow-hidden">
                    <Link2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span className="text-sm font-mono text-slate-700 truncate block">
                      {`${window.location.origin}/kiosk/${campaign.id}`}
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/kiosk/${campaign.id}`);
                      toast.success('Kiosk URL copied!');
                    }}
                    className="shrink-0"
                    title="Copy URL"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`/kiosk/${campaign.id}`, '_blank')}
                    className="shrink-0"
                    title="Open Kiosk Mode"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>

                {/* Short URL - Separate Line */}
                {shortUrls?.kiosk ? (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0 flex items-center gap-2 px-3 py-2 bg-secondary/5 rounded-lg border border-secondary/20 overflow-hidden">
                      <Link2 className="w-4 h-4 text-secondary flex-shrink-0" />
                      <span className="text-sm font-mono text-secondary font-medium truncate block">
                        {shortUrls.kiosk.short_url}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(shortUrls.kiosk.short_url);
                        toast.success('Short URL copied!');
                      }}
                      className="shrink-0 border-secondary/30 text-secondary hover:bg-secondary/10"
                      title="Copy Short URL"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => generateShortUrl('kiosk')}
                    className="text-xs text-secondary p-0 h-auto"
                  >
                    Generate short link
                  </Button>
                )}
                
                <p className="text-xs text-slate-500 mt-2">
                  For on-site registration desk. Requires login.
                </p>
              </div>
            )}

            {/* Registration Window */}
            {isSuperAdmin() && (
              <RegistrationWindowEditor 
                campaign={campaign} 
                onUpdate={fetchCampaignData}
              />
            )}

            <div className="grid grid-cols-2 gap-4">
              {campaign.description && (
                <div className="col-span-2">
                  <p className="text-sm font-jakarta font-medium text-slate-500 mb-1">Description</p>
                  <p className="text-base font-jakarta text-slate-700">{campaign.description}</p>
                </div>
              )}

              <div>
                <p className="text-sm font-jakarta font-medium text-slate-500 mb-1">Created</p>
                <p className="text-base font-jakarta text-slate-900">
                  {format(parseISO(campaign.created_at), 'MMMM dd, yyyy')}
                </p>
              </div>

              {campaign.start_date && (
                <div>
                  <p className="text-sm font-jakarta font-medium text-slate-500 mb-1">Start Date</p>
                  <p className="text-base font-jakarta text-slate-900">
                    {format(parseISO(campaign.start_date), 'MMMM dd, yyyy')}
                  </p>
                </div>
              )}

              {campaign.end_date && (
                <div>
                  <p className="text-sm font-jakarta font-medium text-slate-500 mb-1">End Date</p>
                  <p className="text-base font-jakarta text-slate-900">
                    {format(parseISO(campaign.end_date), 'MMMM dd, yyyy')}
                  </p>
                </div>
              )}

              <div>
                <p className="text-sm font-jakarta font-medium text-slate-500 mb-1">Registration Limit</p>
                <p className="text-base font-jakarta text-slate-900">
                  {campaign.max_registrations ? `${campaign.max_registrations} max` : 'Unlimited'}
                </p>
              </div>
            </div>
          </Card>

          {/* Quick Actions */}
          {isSuperAdmin() && (
            <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-6">
              <h2 className="text-lg font-outfit font-semibold text-slate-900 mb-5">Quick Actions</h2>
              
              {/* ========== FORM & PAGES SECTION ========== */}
              <div className="mb-5">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Layout className="w-3.5 h-3.5" />
                  Form & Pages
                </h3>
                <div className="space-y-2">
                  {/* Landing Page Toggle */}
                  <div className="flex items-center justify-between p-3 bg-slate-50/70 rounded-lg border border-slate-100">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Layout className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-700">Landing Page</p>
                        <p className="text-xs text-slate-500">
                          {campaign.has_landing_page !== false 
                            ? 'Visitors see landing page first' 
                            : 'Visitors go directly to form'}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={campaign.has_landing_page !== false}
                      onCheckedChange={handleToggleLandingPage}
                    />
                  </div>

                  {/* Form Type Toggle */}
                  <FormTypeEditor 
                    campaign={campaign} 
                    onUpdate={fetchCampaignData}
                    onNavigateToBuilder={() => navigate(`/campaigns/${campaignId}/form-builder`)}
                  />

                  {/* Registration Status Toggle */}
                  <RegistrationStatusToggle
                    campaign={campaign}
                    onUpdate={fetchCampaignData}
                  />

                  {/* Only show Manage Form when NOT using external form */}
                  {!campaign.use_external_form && (
                    <Button
                      variant="ghost"
                      onClick={() => navigate(`/campaigns/${campaignId}/form-builder`)}
                      data-testid="manage-form-button"
                      className="w-full justify-start text-slate-600 hover:text-primary hover:bg-primary/5 rounded-lg h-10"
                    >
                      <FormInput className="w-4 h-4 mr-2" />
                      Manage Registration Form
                    </Button>
                  )}

                  {/* Only show Edit Landing Page when landing page is enabled */}
                  {campaign.has_landing_page !== false && (
                    <Button
                      variant="ghost"
                      onClick={() => navigate(`/campaigns/${campaignId}/landing-page`)}
                      data-testid="landing-page-button"
                      className="w-full justify-start text-slate-600 hover:text-primary hover:bg-primary/5 rounded-lg h-10"
                    >
                      <Layout className="w-4 h-4 mr-2" />
                      Edit Landing Page
                    </Button>
                  )}

                  <Button
                    variant="ghost"
                    onClick={() => navigate(`/campaigns/${campaignId}/registrations`)}
                    data-testid="view-registrations-button"
                    className="w-full justify-start text-slate-600 hover:text-primary hover:bg-primary/5 rounded-lg h-10"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    View Registrations
                  </Button>
                </div>
              </div>

              {/* ========== KIOSK & PAYMENTS SECTION ========== */}
              {(campaign.payment_collection_method === 'offline' || campaign.kiosk_enabled || campaign.payment_config?.allow_offline_payments || campaign.payment_collection_method === 'online') && (
                <div className="mb-5">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Banknote className="w-3.5 h-3.5" />
                    Kiosk & Payments
                  </h3>
                  <div className="space-y-2">
                    {/* Kiosk Mode - Show for offline payment collection campaigns or kiosk_enabled */}
                    {(campaign.payment_collection_method === 'offline' || campaign.kiosk_enabled) && (
                      <Button
                        onClick={() => navigate(`/kiosk/${campaignId}`)}
                        className="w-full justify-start bg-primary hover:bg-primary/90 text-white rounded-lg h-10"
                      >
                        <Banknote className="w-4 h-4 mr-2" />
                        Open Kiosk Mode
                      </Button>
                    )}

                    {/* Offline Payment Verification - Show when offline payments are enabled */}
                    {campaign.payment_config?.allow_offline_payments && (
                      <Button
                        variant="ghost"
                        onClick={() => navigate(`/campaigns/${campaignId}/offline-payments`)}
                        className="w-full justify-start text-slate-600 hover:text-primary hover:bg-primary/5 rounded-lg h-10"
                      >
                        <Banknote className="w-4 h-4 mr-2" />
                        Verify Offline Payments
                      </Button>
                    )}

                    {/* Kiosk Mode Toggle - Only for online payment campaigns */}
                    {campaign.payment_collection_method === 'online' && (
                      <div className="flex items-center justify-between p-3 bg-slate-50/70 rounded-lg border border-slate-100">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Banknote className="w-4 h-4 text-primary" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-700">Enable Kiosk Mode</p>
                            <p className="text-xs text-slate-500">Allow on-site cash registration</p>
                          </div>
                        </div>
                        <Switch
                          checked={campaign.kiosk_enabled || false}
                          onCheckedChange={async (checked) => {
                            try {
                              await api.put(`/campaigns/${campaignId}`, { kiosk_enabled: checked });
                              fetchCampaignData();
                              toast.success(checked ? 'Kiosk Mode enabled' : 'Kiosk Mode disabled');
                            } catch (err) {
                              toast.error('Failed to update setting');
                            }
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ========== SETTINGS SECTION ========== */}
              <div className="mb-5">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Edit className="w-3.5 h-3.5" />
                  Settings
                </h3>
                <div className="space-y-2">
                  {/* Show on Homepage Toggle */}
                  <div className="flex items-center justify-between p-3 bg-slate-50/70 rounded-lg border border-slate-100">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Globe className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-700">Show on Homepage</p>
                        <p className="text-xs text-slate-500">Display in public event listing</p>
                      </div>
                    </div>
                    <Switch
                      checked={campaign.show_on_homepage !== false}
                      onCheckedChange={async (checked) => {
                        try {
                          await api.put(`/campaigns/${campaignId}`, { show_on_homepage: checked });
                          fetchCampaignData();
                          toast.success(checked ? 'Event visible on homepage' : 'Event hidden from homepage');
                        } catch (err) {
                          toast.error('Failed to update setting');
                        }
                      }}
                    />
                  </div>

                  <Button
                    variant="ghost"
                    onClick={openTeamModal}
                    className="w-full justify-start text-slate-600 hover:text-primary hover:bg-primary/5 rounded-lg h-10"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Team Member
                  </Button>

                  <Button
                    variant="ghost"
                    onClick={() => navigate(`/campaigns/${campaignId}/edit`)}
                    className="w-full justify-start text-slate-600 hover:text-primary hover:bg-primary/5 rounded-lg h-10"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Event
                  </Button>

                  <Button
                    variant="ghost"
                    onClick={() => navigate(`/campaigns/${campaignId}/settings`)}
                    className="w-full justify-start text-slate-600 hover:text-primary hover:bg-primary/5 rounded-lg h-10"
                  >
                    <Bell className="w-4 h-4 mr-2" />
                    Notifications & Integrations
                  </Button>
                </div>
              </div>

              {/* ========== EXPORT & MORE SECTION ========== */}
              <div>
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Download className="w-3.5 h-3.5" />
                  Export & More
                </h3>
                <div className="space-y-2">
                  <Button
                    variant="ghost"
                    onClick={() => window.open(getPublicUrl(), '_blank')}
                    className="w-full justify-start text-slate-600 hover:text-primary hover:bg-primary/5 rounded-lg h-10"
                  >
                    <Globe className="w-4 h-4 mr-2" />
                    View Public Page
                  </Button>

                  <Button
                    variant="ghost"
                    onClick={async () => {
                      try {
                        const response = await api.get(`/campaigns/${campaignId}/export/csv`, { responseType: 'blob' });
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', `registrations_${campaign.slug}.csv`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        toast.success('CSV exported!');
                      } catch (e) {
                        toast.error('No registrations to export');
                      }
                    }}
                    className="w-full justify-start text-slate-600 hover:text-primary hover:bg-primary/5 rounded-lg h-10"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export Registrations
                  </Button>

                  {/* Archive/Unarchive/Activate Buttons */}
                  {isSuperAdmin() && (
                    <div className="pt-3 mt-3 border-t border-slate-100">
                      {campaign.status === 'archived' ? (
                        <Button
                          variant="ghost"
                          onClick={handleUnarchive}
                          disabled={archiving}
                          className="w-full justify-start text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg h-10"
                        >
                          {archiving ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <RotateCcw className="w-4 h-4 mr-2" />
                          )}
                          Restore Event
                        </Button>
                      ) : campaign.status === 'active' ? (
                        <Button
                          variant="ghost"
                          onClick={() => setShowArchiveModal(true)}
                          className="w-full justify-start text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg h-10"
                        >
                          <Archive className="w-4 h-4 mr-2" />
                          Archive Event
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          onClick={handleActivate}
                          disabled={activating}
                          className="w-full justify-start text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg h-10"
                        >
                          {activating ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <CheckCircle className="w-4 h-4 mr-2" />
                          )}
                          Activate Event
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Team Members Section */}
              {teamMembers.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h3 className="text-sm font-medium text-slate-700 mb-3">Team Members</h3>
                  <div className="space-y-2">
                    {teamMembers.map(member => (
                      <div key={member.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                        <Shield className="w-4 h-4 text-secondary" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{member.name}</p>
                          <p className="text-xs text-slate-500 truncate">{member.email}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Org Admin entry to Event Settings (Vendors tab, etc.) */}
          {!isSuperAdmin() && (
            <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-6">
              <h2 className="text-lg font-outfit font-semibold text-slate-900 mb-3">Event Settings</h2>
              <Button
                variant="outline"
                onClick={() => navigate(`/campaigns/${campaignId}/settings`)}
                className="w-full justify-start"
              >
                <Settings className="w-4 h-4 mr-2" />
                Notifications, Sheets & Vendors
              </Button>
            </Card>
          )}
        </div>

        {/* Fund Management Section - Only for Kaizen Managed campaigns */}
        {campaign.fund_collection_mode === 'kaizen_managed' && (
          <div className="mt-8">
            <FundManagement 
              campaignId={campaignId} 
              campaign={campaign}
            />
          </div>
        )}

        {/* Campaign History Section - Only for Super Admins and Directors */}
        {(isSuperAdmin() || campaign.can_view_history) && (
          <div className="mt-8">
            <EntityHistory
              entityType="campaign"
              entityId={campaignId}
              title="Event History"
            />
          </div>
        )}
      </div>

      {/* Payment Settings Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <Card className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-outfit font-bold">Payment Settings</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowPaymentModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-6">
              {/* Gateway Type Selector */}
              <div className="mb-5">
                <Label className="text-sm font-medium text-slate-700 mb-2 block">
                  Payment Gateway
                </Label>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSelectedGateway('razorpay');
                      setSelectedPaymentAccount('');
                    }}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 text-sm font-medium transition-all ${
                      selectedGateway === 'razorpay'
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    Razorpay
                  </button>
                  <button
                    onClick={() => {
                      setSelectedGateway('instamojo');
                      setSelectedPaymentAccount('');
                    }}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 text-sm font-medium transition-all ${
                      selectedGateway === 'instamojo'
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    Instamojo
                  </button>
                  <button
                    onClick={() => {
                      setSelectedGateway('cashfree');
                      setSelectedPaymentAccount('');
                    }}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 text-sm font-medium transition-all ${
                      selectedGateway === 'cashfree'
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    Cashfree
                  </button>
                </div>
              </div>

              {/* Payment Account Selector */}
              <div className="mb-5">
                <Label className="text-sm font-medium text-slate-700 mb-2 block">
                  Select {selectedGateway === 'razorpay' ? 'Razorpay' : selectedGateway === 'cashfree' ? 'Cashfree' : 'Instamojo'} Account
                </Label>
                <select
                  value={selectedPaymentAccount}
                  onChange={(e) => setSelectedPaymentAccount(e.target.value)}
                  className="w-full h-11 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  <option value="">No Payment (Free Registration)</option>
                  {filteredAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                      {account.is_active ? '' : ' - Inactive'}
                    </option>
                  ))}
                </select>
                {filteredAccounts.length === 0 && (
                  <p className="text-xs text-yellow-600 mt-2">
                    No {selectedGateway} accounts found. Add one in Payment Accounts.
                  </p>
                )}
              </div>

              {selectedPaymentAccount && (
                <div className="mb-5 p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium text-slate-700 mb-2">Selected Account Details</p>
                  {filteredAccounts.filter(a => a.id === selectedPaymentAccount).map(account => (
                    <div key={account.id} className="text-sm text-slate-600">
                      <p><span className="font-medium">Name:</span> {account.name}</p>
                      <p><span className="font-medium">Gateway:</span> {account.gateway_type}</p>
                      <p><span className="font-medium">Status:</span> {account.is_active ? 
                        <span className="text-green-600">Active</span> : 
                        <span className="text-red-500">Inactive</span>
                      }</p>
                    </div>
                  ))}
                </div>
              )}

              {paymentAccounts.length === 0 && (
                <div className="mb-5 p-4 bg-yellow-50 rounded-lg text-center">
                  <p className="text-sm text-yellow-700 mb-2">No payment accounts configured</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowPaymentModal(false);
                      navigate('/payment-accounts');
                    }}
                  >
                    Go to Payment Accounts →
                  </Button>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={savePaymentSettings}
                  disabled={savingPayment}
                  className="flex-1 bg-primary hover:bg-primary-hover"
                >
                  {savingPayment ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Settings'
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Add Team Member Modal */}
      <TeamMemberModal
        isOpen={showTeamModal}
        onClose={() => setShowTeamModal(false)}
        campaignId={campaignId}
        onSave={fetchTeamMembers}
      />

      {/* Archive Confirmation Modal */}
      {showArchiveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <Card className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-outfit font-bold text-slate-900 mb-2">Archive Event</h2>
              <p className="text-slate-600 mb-6">
                Are you sure you want to archive <strong>{campaign?.name}</strong>? 
                The public landing page will be disabled, but all data will be preserved. 
                You can restore it later.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowArchiveModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleArchive}
                  disabled={archiving}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {archiving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Archiving...
                    </>
                  ) : (
                    <>
                      <Archive className="w-4 h-4 mr-2" />
                      Archive
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// Registration Window Editor Component
const RegistrationWindowEditor = ({ campaign, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [startDate, setStartDate] = useState(campaign.registration_start_date || '');
  const [endDate, setEndDate] = useState(campaign.registration_end_date || '');

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toISOString().slice(0, 16);
    } catch {
      return '';
    }
  };

  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return 'Not set';
    try {
      return format(parseISO(dateStr), 'MMM dd, yyyy h:mm a');
    } catch {
      return 'Invalid date';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/campaigns/${campaign.id}`, {
        registration_start_date: startDate || null,
        registration_end_date: endDate || null
      });
      toast.success('Registration window updated');
      setEditing(false);
      onUpdate();
    } catch (error) {
      toast.error('Failed to update registration window');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      await api.put(`/campaigns/${campaign.id}`, {
        registration_start_date: null,
        registration_end_date: null
      });
      setStartDate('');
      setEndDate('');
      toast.success('Registration window cleared - now open indefinitely');
      setEditing(false);
      onUpdate();
    } catch (error) {
      toast.error('Failed to clear registration window');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            <h3 className="text-sm font-semibold text-blue-700">Registration Window</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEditing(true)}
            className="text-blue-600 hover:text-blue-700 hover:bg-blue-100"
          >
            <Edit className="w-4 h-4 mr-1" />
            Edit
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-blue-600 mb-1">Opens</p>
            <p className="text-sm font-medium text-blue-800">
              {formatDateForDisplay(campaign.registration_start_date)}
            </p>
          </div>
          <div>
            <p className="text-xs text-blue-600 mb-1">Closes</p>
            <p className="text-sm font-medium text-blue-800">
              {formatDateForDisplay(campaign.registration_end_date)}
            </p>
          </div>
        </div>
        {!campaign.registration_start_date && !campaign.registration_end_date && (
          <p className="text-xs text-blue-600 mt-2 italic">
            Registration is open indefinitely until manually closed
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
      <div className="flex items-center gap-2 mb-3">
        <Calendar className="w-5 h-5 text-blue-600" />
        <h3 className="text-sm font-semibold text-blue-700">Edit Registration Window</h3>
      </div>
      <p className="text-xs text-blue-600 mb-3">Leave both empty to keep registration open indefinitely</p>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <Label className="text-xs text-blue-700 mb-1 block">Registration Opens</Label>
          <Input
            type="datetime-local"
            value={formatDateForInput(startDate)}
            onChange={(e) => setStartDate(e.target.value)}
            className="h-10 text-sm bg-white"
          />
        </div>
        <div>
          <Label className="text-xs text-blue-700 mb-1 block">Registration Closes</Label>
          <Input
            type="datetime-local"
            value={formatDateForInput(endDate)}
            onChange={(e) => setEndDate(e.target.value)}
            className="h-10 text-sm bg-white"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setEditing(false)}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClear}
          disabled={saving}
          className="text-slate-500 hover:text-slate-700 ml-auto"
        >
          Clear Dates
        </Button>
      </div>
    </div>
  );
};

// Form Type Editor Component - Choose between built form or external link
const FormTypeEditor = ({ campaign, onUpdate, onNavigateToBuilder }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [useExternalForm, setUseExternalForm] = useState(campaign.use_external_form || false);
  const [externalUrl, setExternalUrl] = useState(campaign.external_form_url || '');

  const handleSave = async () => {
    if (useExternalForm && !externalUrl.trim()) {
      toast.error('Please enter an external form URL');
      return;
    }
    
    if (useExternalForm && !externalUrl.startsWith('http')) {
      toast.error('Please enter a valid URL starting with http:// or https://');
      return;
    }

    setSaving(true);
    try {
      await api.put(`/campaigns/${campaign.id}`, {
        use_external_form: useExternalForm,
        external_form_url: useExternalForm ? externalUrl.trim() : null
      });
      toast.success(useExternalForm ? 'External form link saved' : 'Using built-in form');
      setEditing(false);
      onUpdate();
    } catch (error) {
      toast.error('Failed to update form settings');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="flex items-center justify-between p-3 bg-slate-50/70 rounded-lg border border-slate-100 mb-2">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <FileText className="w-4 h-4 text-primary" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-700">Registration Form</p>
            <p className="text-xs text-slate-500">
              {campaign.use_external_form 
                ? 'Using external form link' 
                : 'Using built-in form builder'}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setEditing(true)}
          className="text-primary hover:text-primary hover:bg-primary/10"
        >
          <Edit className="w-4 h-4 mr-1" />
          Change
        </Button>
      </div>
    );
  }

  return (
    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 mb-2">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
          <FileText className="w-4 h-4 text-primary" />
        </div>
        <h3 className="text-sm font-semibold text-slate-700">Registration Form Type</h3>
      </div>
      
      {/* Form Type Selection */}
      <div className="space-y-3 mb-4">
        <label className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 cursor-pointer hover:border-primary/50 transition-colors">
          <input
            type="radio"
            name="formType"
            checked={!useExternalForm}
            onChange={() => setUseExternalForm(false)}
            className="w-4 h-4 text-primary"
          />
          <div>
            <p className="text-sm font-medium text-slate-900">Build Form</p>
            <p className="text-xs text-slate-500">Use the built-in form builder to create your registration form</p>
          </div>
        </label>
        
        <label className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 cursor-pointer hover:border-primary/50 transition-colors">
          <input
            type="radio"
            name="formType"
            checked={useExternalForm}
            onChange={() => setUseExternalForm(true)}
            className="w-4 h-4 text-primary"
          />
          <div>
            <p className="text-sm font-medium text-slate-900">External Form Link</p>
            <p className="text-xs text-slate-500">Use an external form (Google Forms, Typeform, etc.) - opens in new tab</p>
          </div>
        </label>
      </div>

      {/* External URL Input */}
      {useExternalForm && (
        <div className="mb-4">
          <Label className="text-xs text-slate-600 mb-1 block">External Form URL</Label>
          <Input
            type="url"
            value={externalUrl}
            onChange={(e) => setExternalUrl(e.target.value)}
            placeholder="https://forms.google.com/..."
            className="h-10 text-sm bg-white"
          />
          
          {/* Webhook URL for External Payments */}
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs font-semibold text-amber-800 mb-1 flex items-center gap-1">
              <Bell className="w-3 h-3" />
              Payment Webhook URL (for Instamojo)
            </p>
            <p className="text-xs text-amber-700 mb-2">
              Add this URL to your Instamojo payment link to auto-capture registrations:
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-white px-2 py-1.5 rounded border border-amber-300 text-amber-900 overflow-x-auto">
                {`${process.env.REACT_APP_BACKEND_URL}/payments/webhook/instamojo/external/${campaign.slug}`}
              </code>
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 border-amber-300 text-amber-700 hover:bg-amber-100"
                onClick={() => {
                  navigator.clipboard.writeText(
                    `${process.env.REACT_APP_BACKEND_URL}/payments/webhook/instamojo/external/${campaign.slug}`
                  );
                  toast.success('Webhook URL copied!');
                }}
              >
                <Copy className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving}
          className="bg-primary hover:bg-primary/90"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setUseExternalForm(campaign.use_external_form || false);
            setExternalUrl(campaign.external_form_url || '');
            setEditing(false);
          }}
          disabled={saving}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

// Registration Status Toggle Component
const RegistrationStatusToggle = ({ campaign, onUpdate }) => {
  const [saving, setSaving] = useState(false);

  const handleToggle = async (enabled) => {
    setSaving(true);
    try {
      await api.put(`/campaigns/${campaign.id}`, {
        registration_enabled: enabled
      });
      toast.success(enabled ? 'Registration enabled' : 'Registration disabled - visitors will see registration closed message');
      onUpdate();
    } catch (error) {
      toast.error('Failed to update registration status');
    } finally {
      setSaving(false);
    }
  };

  const isEnabled = campaign.registration_enabled !== false;

  return (
    <div className="flex items-center justify-between p-3 bg-slate-50/70 rounded-lg border border-slate-100 mb-2">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isEnabled ? 'bg-emerald-100' : 'bg-red-100'}`}>
          {isEnabled ? (
            <CheckCircle className="w-4 h-4 text-emerald-600" />
          ) : (
            <AlertTriangle className="w-4 h-4 text-red-500" />
          )}
        </div>
        <div>
          <p className="text-sm font-medium text-slate-700">Registration</p>
          <p className={`text-xs ${isEnabled ? 'text-emerald-600' : 'text-red-500'}`}>
            {isEnabled 
              ? 'Open for registrations' 
              : 'Closed - visitors see registration closed page'}
          </p>
        </div>
      </div>
      <Switch
        checked={isEnabled}
        onCheckedChange={handleToggle}
        disabled={saving}
      />
    </div>
  );
};

export default CampaignDetail;
